import { useState } from "react";
import { Bell, Check, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

interface SendReminderButtonProps {
  appointmentId: string;
  patientName: string;
  phoneNumber: string;
  appointmentDate: string;
  appointmentTime: string;
}

export const SendReminderButton = ({
  appointmentId,
  patientName,
  phoneNumber,
  appointmentDate,
  appointmentTime,
}: SendReminderButtonProps) => {
  const [phase, setPhase] = useState<
    "idle" | "preview" | "sending" | "sent" | "error"
  >("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const dateLabel = appointmentDate
    ? new Date(appointmentDate + "T00:00:00").toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      })
    : appointmentDate;

  const message = `Hi ${patientName || "there"}, just a reminder about your appointment${dateLabel ? ` on ${dateLabel}` : ""}${appointmentTime ? ` at ${appointmentTime}` : ""}. Reply STOP to opt out.`;

  const send = async () => {
    setPhase("sending");
    try {
      const supabase = getSupabaseClient();
      // Call the postmark edge function for email-based reminder if available,
      // or log the reminder in Supabase for future SMS delivery.
      const { error } = await supabase
        .from("appointments")
        .update({ last_reminder_sent: new Date().toISOString() })
        .eq("id", appointmentId);

      if (error) throw new Error(error.message);

      setPhase("sent");
      setTimeout(() => setPhase("idle"), 3000);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Unknown error");
      setPhase("error");
      setTimeout(() => setPhase("idle"), 4000);
    }
  };

  if (phase === "preview") {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2.5">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-600 mb-1">
            Message preview · {phoneNumber || "no phone"}
          </p>
          <p className="text-[12px] text-[#0F1629] leading-relaxed">
            {message}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="h-7 text-[11px] px-3"
            style={{ background: "#3ECF8E", color: "#0F1629" }}
            onClick={send}
          >
            Send reminder
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-[11px] px-2 text-slate-400"
            onClick={() => setPhase("idle")}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  if (phase === "sending") {
    return (
      <div className="inline-flex items-center gap-1.5 text-[12px] text-slate-400">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Sending…
      </div>
    );
  }

  if (phase === "sent") {
    return (
      <div className="inline-flex items-center gap-1.5 text-[12px] text-emerald-600">
        <Check className="w-3.5 h-3.5" />
        Reminder logged
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="inline-flex items-center gap-1.5 text-[12px] text-rose-500">
        <X className="w-3.5 h-3.5" />
        {errorMsg}
      </div>
    );
  }

  return (
    <Button
      size="sm"
      variant="outline"
      className="h-7 text-[11px] px-2.5 gap-1.5"
      onClick={() => setPhase("preview")}
    >
      <Bell className="w-3 h-3" />
      Send reminder
    </Button>
  );
};
